import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('tt_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function formatError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(' ');
  return String(detail);
}

export const api = {
  register: (data) => client.post('/auth/register', data),
  login: (data) => client.post('/auth/login', data),
  me: () => client.get('/auth/me'),
  categories: () => client.get('/categories'),
  docTypes: () => client.get('/doc-types'),

  listTrades: (params) => client.get('/trades', { params }),
  getTrade: (id) => client.get(`/trades/${id}`),
  getReviews: (id) => client.get(`/trades/${id}/reviews`),
  postReview: (id, data) => client.post(`/trades/${id}/reviews`, data),

  // Payments (Stripe Checkout)
  createCheckout: (lookupKey) => client.post('/payments/checkout', { lookup_key: lookupKey, origin_url: window.location.origin }),
  paymentStatus: (sessionId) => client.get(`/payments/status/${sessionId}`),
  tradieSubscription: () => client.get('/tradie/subscription'),

  myTradieProfile: () => client.get('/tradie/me'),
  upsertTradieProfile: (data) => client.post('/tradie/profile', data),
  setAvailable: (available) => client.patch(`/tradie/available?available=${available}`),

  // Compliance
  myCompliance: () => client.get('/compliance/me'),
  uploadCompliance: (data) => client.post('/compliance', data),
  deleteCompliance: (id) => client.delete(`/compliance/${id}`),
  getTradieCompliance: (tradieId) => client.get(`/compliance/tradie/${tradieId}`),

  // Client reviews / warnings
  searchClientReviews: (q) => client.get('/client-reviews', { params: { q } }),
  postClientReview: (data) => client.post('/client-reviews', data),
  clientFlags: () => client.get('/client-reviews/flags'),

  // Call feedback (anonymous)
  callFeedback: (data) => client.post('/call-feedback', data),

  // Job history (tradies-only ledger)
  addJobHistory: (data) => client.post('/job-history', data),
  searchJobHistory: (q) => client.get('/job-history', { params: { q } }),

  // Agency properties
  listProperties: () => client.get('/properties'),
  createProperty: (data) => client.post('/properties', data),
  saveTradieToProperty: (propId, tradieId) => client.post(`/properties/${propId}/save-tradie?tradie_id=${tradieId}`),
  deleteProperty: (id) => client.delete(`/properties/${id}`),

  adminPending: () => client.get('/admin/pending'),
  adminVerify: (id) => client.post(`/admin/verify/${id}`),

  suggestCategory: (description) => client.post('/ai/suggest-category', { description }),

  // Agency preferred panel
  preferredPanel: () => client.get('/agent/preferred-panel'),
  addPreferred: (tradieId) => client.post(`/agent/preferred-panel?tradie_id=${tradieId}`),
  removePreferred: (tradieId) => client.delete(`/agent/preferred-panel/${tradieId}`),

  // Trade Deals (affiliate hub)
  listTradeDeals: (category) => client.get('/trade-deals', { params: category ? { category } : {} }),
  clickTradeDeal: (id) => client.post(`/trade-deals/${id}/click`),

  // SEO suburb index
  seoSuburbIndex: () => client.get('/seo/suburb-index'),

  // Agency plans / subscription
  agencyPlans: () => client.get('/agency/plans'),
  mySubscription: () => client.get('/agency/subscription'),
  agencySubscribeInterest: (plan_id) => client.post('/agency/subscribe/interest', { plan_id }),
  enterpriseInquiry: (payload) => client.post('/agency/enterprise-inquiry', payload),

  // Unclaimed listings
  claimListing: (tradie_id, business_name_confirm) => client.post('/tradies/claim', { tradie_id, business_name_confirm }),
  requestRemoval: (payload) => client.post('/tradies/removal-request', payload),
  nominateTradie: (payload) => client.post('/tradies/nominate', payload),

  // AI photo quote + tradie of the month
  photoQuote: (payload) => client.post('/ai/photo-quote', payload),
  tradieOfTheMonth: () => client.get('/tradie-of-the-month'),
  tradieHeartbeat: () => client.post('/tradie/heartbeat'),
  neighbourCount: (tradie_id, suburb) => client.get(`/trades/${tradie_id}/neighbour-count`, { params: suburb ? { suburb } : {} }),

  // Admin console
  adminDashboard: () => client.get('/admin/dashboard'),
  adminClaimRequests: () => client.get('/admin/claim-requests'),
  adminRemovalRequests: () => client.get('/admin/removal-requests'),
  adminResolveRemoval: (id, action) => client.post(`/admin/removal-requests/${id}/resolve?action=${action}`),
  adminNominations: () => client.get('/admin/nominations'),
  adminEnterpriseInquiries: () => client.get('/admin/enterprise-inquiries'),

  // ---------- Sprint 1: Property Passport + Jobs + Risk & Trust v2 ----------
  pnCreateProperty: (payload) => client.post('/pn/properties', payload),
  pnListProperties: () => client.get('/pn/properties'),
  pnPropertyPassport: (id) => client.get(`/pn/properties/${id}/passport`),
  pnPortfolioSummary: () => client.get('/pn/portfolio/summary'),

  pnCreateJob: (payload) => client.post('/pn/jobs', payload),
  pnListJobs: (params) => client.get('/pn/jobs', { params }),
  pnSubmitQuote: (jobId, payload) => client.post(`/pn/jobs/${jobId}/quotes`, payload),
  pnListQuotes: (jobId) => client.get(`/pn/jobs/${jobId}/quotes`),
  pnApproveQuote: (jobId, quoteId) => client.post(`/pn/jobs/${jobId}/approve/${quoteId}`),
  pnCompleteJob: (jobId) => client.post(`/pn/jobs/${jobId}/complete`),

  pnRiskTrustCategories: () => client.get('/pn/risk-trust/categories'),
  pnCreateRiskTrust: (payload) => client.post('/pn/risk-trust/entries', payload),
  pnListMyRiskTrust: () => client.get('/pn/risk-trust/entries'),
  pnRiskTrustLookup: (params) => client.get('/pn/risk-trust/lookup', { params }),
  pnDisputeEntry: (payload) => client.post('/pn/risk-trust/dispute', payload),

  // Sprint 2: Attachments · Panel · Notifications · My Property
  pnUploadAttachment: (jobId, payload) => client.post(`/pn/jobs/${jobId}/attachments`, payload),
  pnListAttachments: (jobId) => client.get(`/pn/jobs/${jobId}/attachments`),
  pnFetchAttachment: (attId) => client.get(`/pn/attachments/${attId}`),
  pnPanelInvite: (payload) => client.post('/pn/panel/invite', payload),
  pnPanelList: () => client.get('/pn/panel'),
  pnPanelAction: (memberId, action) => client.post(`/pn/panel/${memberId}/action?action=${action}`),
  pnNotifications: () => client.get('/pn/notifications'),
  pnMyPropertySummary: () => client.get('/pn/my-property/summary'),

  // Smart search — plain English → correct trade
  smartSearch: (q) => client.get('/smart-search', { params: { q } }),
  smartSearchExamples: () => client.get('/smart-search/examples'),

  // Voice · Photo · Chat AI helpers
  voiceTranscribe: (formData) => client.post('/voice/transcribe', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  photoDiagnose: (payload) => client.post('/ai/photo-diagnose', payload),
  aiChat: (payload) => client.post('/ai/chat', payload),

  // Public Passport share
  pnCreateShare: (propId, payload) => client.post(`/pn/properties/${propId}/share`, payload),
  pnListShares: (propId) => client.get(`/pn/properties/${propId}/shares`),
  pnRevokeShare: (token) => client.post(`/pn/properties/shares/${token}/revoke`),
  publicPassportShare: (token) => client.get(`/passport/share/${token}`),
};

export default client;
